import { ArrowBack, Download, EditOutlined } from '@mui/icons-material'
import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import React, { Context } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Chip } from 'src/components/common/Chip'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { Spinner } from 'src/components/Spinner'
import { IDeviation } from 'src/generated-types/deviation'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { getThemeColor } from 'src/utils'
import { permissionValue } from 'src/enums/permissionValue'
import { CreateWorkOrderFromDeviationButton } from '../CreateWorkOrderFromDeviationButton'
import { FollowButton } from 'src/components/notifications/FollowButton'

type Props = {
  customerId?: EntityId
  localityId?: EntityId
  data?: IDeviation
  /** Pre-resolved type label(s) for the meta line (e.g. visual/measured deviation) */
  typeLabels?: string[]
  /** Name of the user that closed the deviation (when status === true) */
  closedBy?: string
  isAdminLayout?: boolean
  context?: Context<any> | null
  isDownloadingPDF?: boolean
  onDownloadPDF: () => void
  onEdit: (deviation: IDeviation) => void
}

export const DeviationHeaderBar: React.FC<Props> = ({
  customerId,
  localityId,
  data,
  typeLabels,
  closedBy,
  isAdminLayout,
  context,
  isDownloadingPDF,
  onDownloadPDF,
  onEdit,
}) => {
  const { t } = useTranslation(['deviation', 'common'])
  const theme = useTheme()
  const navigate = useNavigate()

  const metaParts = [
    data?.responsiblePersonName
      ? `${t('responsiblePerson', { ns: 'deviation' })}: ${data.responsiblePersonName}`
      : undefined,
  ].filter(Boolean) as string[]

  return (
    <Box
      sx={{
        backgroundColor: getThemeColor(theme, MUIThemeColors.white),
        padding: '12px 16px',
        borderRadius: '8px',
        border: '1px solid #E6E8EA',
      }}
    >
      <Flex.Column gap={0.75} sx={{ width: '100%' }}>
        {/* Row 1: back + title + #id chip | action buttons */}
        <Flex.Row
          sx={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', width: '100%' }}
          gap={1}
        >
          <Flex.Row sx={{ alignItems: 'center', minWidth: 0 }} gap={1}>
            <IconButton size="small" onClick={() => navigate(-1)} aria-label={t('back', { ns: 'common' })}>
              <ArrowBack fontSize="small" />
            </IconButton>
            {data?.id != null && <Chip size="small" label={`#${data.id}`} variant="outlined" />}
            <Tooltip title={data?.title ?? ''} placement="top-start" enterDelay={300}>
              <Typography
                sx={{
                  fontSize: '20px',
                  fontWeight: 600,
                  color: getThemeColor(theme, MUIThemeColors.primaryDark),
                  minWidth: 0,
                }}
              >
                {data?.title ?? ''}
              </Typography>
            </Tooltip>
          </Flex.Row>

          <Flex.Row gap={1} sx={{ alignItems: 'center', flexWrap: 'wrap', ml: 'auto' }}>
            {customerId && data?.id != null && (
              <FollowButton customerId={customerId} entityType="Deviation" entityId={data.id} />
            )}
            <GridActionButton
              buttonText={t('downloadPdf', { ns: 'common' })}
              buttonIcon={
                isDownloadingPDF ? <Spinner size={'20px'} /> : <Download sx={{ height: '20px', width: '20px' }} />
              }
              onClick={onDownloadPDF}
              disabled={isDownloadingPDF}
              context={context}
              variant={'outlined'}
              shadowWeight={null}
              width="auto"
              size="small"
            />
            {!isAdminLayout && customerId && localityId && data?.id != null && (
              <CreateWorkOrderFromDeviationButton
                customerId={customerId}
                localityId={localityId}
                deviation={{ id: data.id, title: data.title, description: data.description, status: data.status }}
              />
            )}
            {data?.status ? (
              <Chip
                variant="outlined"
                label={`${t('closedBy', { ns: 'common' })}: ${closedBy ?? ''}`}
                size="small"
                sx={{
                  height: '32px',
                  fontSize: '14px',
                  px: 0.5,
                  borderColor: 'success.main',
                  color: 'success.main',
                }}
              />
            ) : (
              <GridActionButton
                buttonText={t('edit', { ns: 'common' })}
                buttonIcon={<EditOutlined sx={{ height: '20px', width: '20px' }} />}
                onClick={() => data && onEdit(data)}
                disabled={data?.status}
                permissionBinaryValues={
                  isAdminLayout
                    ? undefined
                    : [
                        permissionValue.Locality.Deviation.Update,
                        permissionValue.Locality.MainComponent.Deviation.Write,
                        permissionValue.ClientAdminPermission,
                        permissionValue.ServiceStation.Write,
                        permissionValue.ServiceStation.Update,
                      ]
                }
                context={context}
                variant={'outlined'}
                shadowWeight={null}
                width="auto"
                size="small"
              />
            )}
          </Flex.Row>
        </Flex.Row>

        {/* Row 2: meta line — linked work orders live in the context sidebar, not here */}
        <Box>
          {metaParts.length > 0 && (
            <Typography sx={{ fontSize: '13px', color: getThemeColor(theme, MUIThemeColors.secondaryMain) }}>
              {metaParts.join(' · ')}
            </Typography>
          )}
        </Box>
      </Flex.Column>
    </Box>
  )
}
